'use client'

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'

interface LanguageButtonProps {
  currentLanguage: 'en' | 'vn' | 'fr'
  onClick: (lang: 'en' | 'vn' | 'fr') => void
}

const LANGUAGES = [
  { code: 'vn', label: 'Tiếng Việt', flag: '/images/flag-vn.png' },
  { code: 'en', label: 'English', flag: '/images/flag-en.png' },
  { code: 'fr', label: 'Francais', flag: 'https://upload.wikimedia.org/wikipedia/en/c/c3/Flag_of_France.svg' },
]

const LanguageButton: React.FC<LanguageButtonProps> = ({
  currentLanguage,
  onClick
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const selected = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0]

  return (
    <div className="relative w-full" ref={ref}>
      <button
        className="flex items-center w-full px-4 py-3 rounded-4xl bg-white shadow-md hover:bg-gray-50 transition justify-between"
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        <div className="flex items-center gap-3">
          <Image
            src={selected.flag}
            alt={``}
            width={32}
            height={32}
            className="rounded-full object-cover"
          />
          <span className="font-semibold text-gray-700 text-lg">Chọn ngôn ngữ</span>
        </div>
        <svg className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`} width="18" height="18" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L5 5L9 1" stroke="#656565" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white rounded-2xl shadow-xl z-50 p-2 flex flex-col gap-2 animate-fade-in">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className="flex items-center justify-between w-full px-2 py-2 rounded-xl hover:bg-gray-100 transition"
              onClick={() => { onClick(lang.code as 'en' | 'vn' | 'fr'); setOpen(false); }}
              type="button"
            >
              <div className="flex items-center gap-3">
                <Image
                  src={lang.flag}
                  alt={``}
                  width={32}
                  height={32}
                  className="rounded-full object-cover"
                />
                <span className="font-semibold text-gray-700 text-lg">{lang.label}</span>
              </div>
              {currentLanguage === lang.code ? (
                <span className="w-6 h-6 flex items-center justify-center bg-green-400 rounded-full shadow">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 9L8 12L13 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              ) : (
                <span className="w-6 h-6 flex items-center justify-center border-2 border-gray-300 rounded-full shadow-sm"></span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageButton
